import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FlashcardService } from 'src/app/core/flashcard.service';
import { LearnsetService } from 'src/app/core/learnset.service';
import { OnboardingService } from 'src/app/core/onboarding.service';
import { PremiumFeatureDialogService } from 'src/app/core/premium-feature-dialog.service';
import { UserService } from 'src/app/core/user.service';
import { Flashcard } from 'src/app/models/flashcard.model';
import { Learnset } from 'src/app/models/learnset.model';
import { Onboarding } from 'src/app/models/onboarding.model';
import { User } from 'src/app/models/user.model';

@Component({
  selector: 'app-learn-view',
  templateUrl: './learn-view.component.html',
  styleUrls: ['./learn-view.component.scss'],
  standalone: false
})
export class LearnViewComponent {
  
  onboarding?: Onboarding | null;
  user?: User;

  learnset?: Learnset;
  learnsetId?: string;

  stack?: Flashcard[];
  side: 'front' | 'back' = 'front';
  totalCount: number = 0;

  isLoading: boolean = false;
  currentExplanation: any;
  explanationVisible: boolean = false;
  isLoadingExplanation: boolean = false;

  learnsetHasMoreCards: boolean = true;

  introductionVisible: boolean = false;
  premiumPromptVisible: boolean = false;

  sm2ButtonsDisabled: boolean = false;
  currentLoadingScoreButton: number | null = null;

  smartRepetitionEnabled: boolean = true;

  benefits: any[] = [
    {
      icon: 'brain',
      title: 'Schalte 2 weitere smarte Lernmodi frei',
      description: 'Schalte den Textantwort-Modus und Audio-Modus frei und lasse dir von KI Feedback geben.'
    },
    {
      icon: 'cards',
      title: 'Keine Limits beim Erstellen von Karteikarten mit KI',
      description: 'Lasse dir Karteikarten blitzschnell aus deinen Dateien mit mehr als 20 Seiten erstellen.'
    },
    {
      icon: 'click',
      title: 'Ausführliche Erklärungen einen Klick entfernt',
      description: 'Lasse dir beim Wiederholen von Lernkarten auf Knopfdruck eine ausführliche KI-Erklärung anzeigen (verfügbar in allen 3 Lernmodi).'
    },
    {
      icon: 'file-ai',
      title: 'Teste dein Wissen mit KI-Probeklausuren',
      description: 'Lasse dir auf Knopfdruck Probeklausuren auf Basis deiner Lernsets generieren, absolviere sie und erhalte im Anschluss von unserer KI eine Note und Feedback zu deinen Antworten. Sehr hilfreich zur Prüfungsvorbereitung!'
    },
    {
      icon: 'confetti',
      title: '+2 weitere Vorteile'
    }
  ];

  constructor(
    private learnsetService: LearnsetService,
    private flashcardService: FlashcardService,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private premiumFeatureService: PremiumFeatureDialogService,
    private onboardingService: OnboardingService
  ) {}

  ngOnInit(): void {
    const smartRepetitionParam = this.route.snapshot.queryParamMap.get('smartRepetition');
    this.smartRepetitionEnabled = smartRepetitionParam === 'true';

    this.learnsetId = this.route.snapshot.paramMap.get('id')!;
    this.fetchLearnset(this.learnsetId!);
    this.userService.getUser().subscribe((user) => this.user = user);
    this.onboardingService.onboarding$.subscribe((onboarding) => {
      this.onboarding = onboarding;
      if (onboarding?.classicModeDialogShown === false) {
        setTimeout(() => {
          this.introductionVisible = true;
        }, 1000);
      }
    });
  }

  fetchLearnset(learnsetId: string): void {
    this.learnsetService.getLearnset(learnsetId)
    .subscribe((learnset: any) => {
      this.learnset = Learnset.constructLearnset(learnset);

      // Filtere die Karten, die fällig sind
      const filteredCards = this.learnset.flashcards.filter(
        (flashcard) => flashcard.dueDate! < new Date()
      );

      if (this.smartRepetitionEnabled) {
        // Wähle nur die ersten 15 Karten aus
        this.stack = JSON.parse(JSON.stringify(filteredCards.slice(0, 15)));
      } else {
        this.stack = JSON.parse(JSON.stringify(this.learnset.flashcards));
      }

      // Setze den totalCount auf die Anzahl der ausgewählten Karten
      this.totalCount = this.stack!.length;

      // Überprüfe, ob es noch mehr Karten gibt, und setze den boolean entsprechend
      if (this.smartRepetitionEnabled) {
        this.learnsetHasMoreCards = filteredCards.length > this.stack!.length;
      } else {
        this.learnsetHasMoreCards = false;
      }
    });
  }

  changeSide() {
    this.side = this.side === 'front' ? 'back' : 'front';
    this.sm2ButtonsDisabled = this.side === 'front';
  }

  next(grade: number) {
    this.isLoading = true;
    let flashcard: Flashcard = this.stack![0];
    this.sm2ButtonsDisabled = true;

    if (this.smartRepetitionEnabled) {
      this.currentLoadingScoreButton = grade;
      this.flashcardService.updateFlashcardAfterPractice(flashcard, true, grade).subscribe((result) => {
        flashcard = result;
        
        if (flashcard.dueDate! < new Date()) {
          this.stack!.splice(0, 1);
          this.stack?.splice(9, 0, flashcard);
        } else {
          this.stack!.splice(0, 1);
        }
        this.side = 'front';
        this.isLoading = false;
        this.currentLoadingScoreButton = null;

        if (this.stack?.length === 0 && !this.user!.upgradePromptAfterFirstRevisionShown && !this.user!.isPremiumUser) {
          setTimeout(() => {
            this.premiumPromptVisible = true;
          }, 2000);
        }
      });
    } else {
      this.flashcardService.updateFlashcardAfterPractice(flashcard, false, grade).subscribe((result) => {
        this.stack!.splice(0, 1);
        this.side = 'front';
        this.isLoading = false;
        this.currentLoadingScoreButton = null;
      });
    }
  }

  finishSession() {
    this.userService.fetchRepeatCardsCount();
    this.router.navigate(['/sets/' + this.learnsetId]);
  }

  continueSession() {
    this.fetchLearnset(this.learnsetId!);
  }

  showExplanation() {
    if (this.user && this.user.isPremiumUser) {
      this.isLoadingExplanation = true;
      this.flashcardService.generateAiExplanation(this.stack![0].id!).subscribe((result) => {
        this.currentExplanation = result.result;
        this.isLoadingExplanation = false;
        this.explanationVisible = true;
      });
    } else {
      this.premiumFeatureService.showDialog('Ausführliche Erklärung zu dieser Lernkarte', 'Upgrade auf Premium, um dir eine ausführliche Erklärung dieser Lernkarte anzeigen zu lassen.');
    }
  }

  introFinished() {
    this.introductionVisible = false;
    this.onboarding!.classicModeDialogShown = true;
    this.onboardingService.updateOnboarding(this.onboarding!);
  }

  premiumPopupCancelled() {
    this.premiumPromptVisible = false;
    this.userService.setPremiumPromptShown().subscribe();
  }

  navToGetPremium() {
    this.router.navigate(['get-premium']);
  }
}
